import { RuntimeEventData, InitialValue } from '@/app/modules/Metrics/types';
import { deepSerializationCopy } from '@/app/tools/objectUtils';
import { Cloneable } from '@/app/tools/Cloneable';

/**
 * Позволяет задать произвольную логику вычисления конечного значения
 *
 * @see RuntimeEventData
 * @see InitialValue
 */
export type ValueHolderCallback = (
    this: ValueHolder,
    runtimeEventData: RuntimeEventData,
    initialValue: InitialValue
) => RuntimeEventData & InitialValue;

/**
 * Структура. Используется для хранения в карте событий значений, вычисляемых по время вызова.
 * Может хранить начальное значение {@see InitialValue}, колбэк кастомной обработки значений {@see ValueHolderCallback}
 * и runtime-данные {@see RuntimeEventData} события.
 * Так-же, если {@see InitialValue} и {@see RuntimeEventData} являются объектами, а колбэк {@see ValueHolderCallback} не задан,
 * то эти объекты будут смержены.
 */
export default class ValueHolder extends Cloneable {
    protected runtimeValue: RuntimeEventData;

    constructor(
        protected initialValue: InitialValue,
        public readonly callback?: ValueHolderCallback
    ) {
        super();
    }

    /**
     * Клонируем объект, для того чтобы не менять основной
     */
    public clone() {
        const { initialValue, callback } = this;

        const clonedValue =
            typeof initialValue === 'object'
                ? deepSerializationCopy(initialValue)
                : initialValue;

        return new ValueHolder(clonedValue, callback);
    }

    /**
     * Задаем runtime значение
     *
     * @private
     */
    public _setRuntimeValue(runtimeEventData: RuntimeEventData) {
        if (typeof runtimeEventData === 'object') {
            this.runtimeValue = deepSerializationCopy(runtimeEventData);
        } else {
            this.runtimeValue = runtimeEventData;
        }
    }

    /**
     * Получаем конечное значение, сформированное из начального и runtime значений
     *
     * Функция callback используется для кастомного наполнения начального значения данными
     * Если нет функции, но есть оба значения, и они оба - объекты, то можно их смержить.
     * Простые начальные значения, не имеющие runtime-данных возвращаются как есть.
     * Остальное не имеет смысла
     *
     * @private
     */
    public _getValue() {
        if (typeof this.callback === 'function') {
            return this.callback(this.runtimeValue, this.initialValue);
        } else {
            if (
                typeof this.runtimeValue === 'object' &&
                typeof this.initialValue === 'object'
            ) {
                return Object.assign({}, this.initialValue, this.runtimeValue);
            } else {
                return this.initialValue;
            }
        }
    }
}
